import { useState, type ButtonHTMLAttributes, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { palette, radius, space, typography } from "@ux/tokens";
import { useViewport } from "@ux/hooks";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

export interface ButtonProps
    extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "style"> {
    readonly variant?: ButtonVariant;
    readonly size?: ButtonSize;
    readonly grow?: boolean;
    readonly leadingIcon?: ReactNode;
    readonly trailingIcon?: ReactNode;
    readonly children?: ReactNode;
}

export function Button({
    variant = "primary",
    size = "md",
    grow = false,
    leadingIcon,
    trailingIcon,
    children,
    disabled,
    ...rest
}: ButtonProps): ReactElement {
    const [hover, setHover] = useState(false);
    const { isPhone } = useViewport();

    const surface = resolveSurface(variant, hover, disabled === true);
    const sizeStyle = resolveSize(size, isPhone);

    const style: CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: space[2],
        borderRadius: radius.md,
        fontWeight: typography.weight.semibold,
        fontSize: sizeStyle.fontSize,
        padding: sizeStyle.padding,
        // 48-px touch-target floor on phone (covers Apple's 44pt and
        // Material's 48dp guidelines in one shot). On desktop the
        // existing compact heights are preserved.
        minHeight: sizeStyle.minHeight,
        cursor: disabled === true ? "not-allowed" : "pointer",
        opacity: disabled === true ? 0.55 : 1,
        transition: "background 120ms ease, color 120ms ease, border-color 120ms ease",
        flex: grow ? 1 : undefined,
        ...surface,
    };

    return (
        <button
            type="button"
            {...rest}
            disabled={disabled}
            style={style}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {leadingIcon}
            {children !== undefined ? <span>{children}</span> : null}
            {trailingIcon}
        </button>
    );
}

function resolveSurface(
    variant: ButtonVariant,
    hover: boolean,
    isDisabled: boolean,
): Pick<CSSProperties, "background" | "color" | "border"> {
    if (isDisabled) {
        return {
            background: palette.surface,
            color: palette.fgMuted,
            border: `1px solid ${palette.border}`,
        };
    }
    switch (variant) {
        case "primary":
            return {
                background: hover ? "oklch(0.72 0.14 260)" : palette.accent,
                color: "oklch(0.18 0.03 260)",
                border: `1px solid ${palette.accent}`,
            };
        case "secondary":
            return {
                background: hover ? palette.surfaceRaised : palette.surface,
                color: palette.fg,
                border: `1px solid ${palette.border}`,
            };
        case "ghost":
            return {
                background: hover ? palette.surface : "transparent",
                color: palette.fg,
                border: "1px solid transparent",
            };
        case "danger":
            return {
                background: hover ? "oklch(0.70 0.18 25 / 0.28)" : palette.dangerBg,
                color: palette.danger,
                border: `1px solid oklch(0.65 0.18 25 / 0.45)`,
            };
    }
}

function resolveSize(
    size: ButtonSize,
    isPhone: boolean,
): { fontSize: string; padding: string; minHeight: string } {
    switch (size) {
        case "sm":
            return {
                fontSize: typography.size.sm,
                padding: isPhone ? `10px 14px` : `6px 12px`,
                minHeight: isPhone ? "44px" : "auto",
            };
        case "md":
            return {
                fontSize: typography.size.md,
                padding: isPhone ? `12px 18px` : `9px 16px`,
                minHeight: isPhone ? "48px" : "auto",
            };
    }
}
